'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getImageProxyUrl } from '@/shared/lib/proxy-url';
import CloseIcon from '@ncai-axe/react-ui-v3/assets/svg/icons/ic_close.svg';
import styles from './styles.module.scss';

type Props = {
  src: string;
  alt?: string;
  onClose: () => void;
};

const MIN_SCALE = 0.1;
const MAX_SCALE = 5;
const ZOOM_STEP = 0.1;
const BADGE_HIDE_DELAY = 1500;

export default function ImageViewer({ src, alt = '', onClose }: Props) {
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [showBadge, setShowBadge] = useState(false);

  const dragStartRef = useRef({ x: 0, y: 0 });
  const translateStartRef = useRef({ x: 0, y: 0 });
  const badgeTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const handleWheel = useCallback(
    (e: React.WheelEvent) => {
      e.stopPropagation();
      const direction = e.deltaY < 0 ? 1 : -1;
      const newScale = Math.min(
        MAX_SCALE,
        Math.max(MIN_SCALE, scale + direction * ZOOM_STEP * scale),
      );
      setScale(newScale);

      setShowBadge(true);
      if (badgeTimerRef.current) clearTimeout(badgeTimerRef.current);
      badgeTimerRef.current = setTimeout(
        () => setShowBadge(false),
        BADGE_HIDE_DELAY,
      );
    },
    [scale],
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0) return;
      e.preventDefault();
      setIsDragging(true);
      dragStartRef.current = { x: e.clientX, y: e.clientY };
      translateStartRef.current = { ...translate };
    },
    [translate],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging) return;
      const dx = e.clientX - dragStartRef.current.x;
      const dy = e.clientY - dragStartRef.current.y;
      setTranslate({
        x: translateStartRef.current.x + dx,
        y: translateStartRef.current.y + dy,
      });
    },
    [isDragging],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose],
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (badgeTimerRef.current) clearTimeout(badgeTimerRef.current);
    };
  }, [onClose]);

  const zoomPercent = Math.round(scale * 100);

  return createPortal(
    <div
      className={styles.backdrop}
      onClick={handleBackdropClick}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      <img
        className={styles.image}
        src={getImageProxyUrl({ src })}
        alt={alt}
        draggable={false}
        style={{
          transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`,
          cursor: isDragging ? 'grabbing' : 'grab',
        }}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
      />
      <button
        className={styles.closeButton}
        onClick={onClose}
        aria-label="close"
        type="button"
      >
        <CloseIcon width={32} height={32} />
      </button>
      {showBadge && <div className={styles.zoomBadge}>{zoomPercent}%</div>}
    </div>,
    document.body,
  );
}
